import { RefObject } from 'react';
import { apiFileUpload } from 'api/ZimssaAPI';
import { IOptionType } from 'components/atoms/SelectBox';
import { MEGA_BYTE_10 } from 'commons/ZimssaConstant';
import {
    ALERT_IMG_UPLOAD_FAIL,
    ALERT_IMG_UPLOAD_NO_SEARCH,
    ALERT_IMG_UPLOAD_SIZE,
    ALERT_IMG_UPLOAD_TYPE,
    ALERT_IMG_UPLOAD_EXTENSION,
  } from 'commons/ZimssaString';
  import { ImageObject } from '../components/molecules/FileImgListUploader';

/**
 * 년 생성
 * @param term
 */
export function getYear(term: number): Array<IOptionType> {
    const years = Array<IOptionType>();
    const currentYear = new Date().getFullYear();

    for (let year = currentYear; year >= currentYear - term; year--) {
        years.push({ name: year.toString(), value: year });
    }

    return years;
}

/**
 * 월 생성
 */
export function getMonth(): Array<IOptionType> {
    const months = Array<IOptionType>();

    for (let month = 1; month <= 12; month++) {
        months.push({ name: month.toString(), value: month });
    }
    return months;
}

/**
 * 일 생성
 * @param year
 * @param month
 */
export function getDays(year: number, month: number): Array<IOptionType> {
    const days = Array<IOptionType>();

    const lastDay = new Date(year, Number(month), 0).getDate();

    for (let day = 1; day <= lastDay; day++) {
        days.push({ name: day.toString(), value: day });
    }

    return days;
}

/**
 * 상수 배열을 키/밸류로 리턴
 * @param array
 */
export function constToKeyValue(array: Array<any>): Array<{ key: string; value: string }> {
    const resultArray = Array<{ key: string; value: string }>();

    array.forEach((item) => {
        resultArray.push({ key: item.toString(), value: item.toString() });
    });

    return resultArray;
}

/**
 * json 데이터를 formData로
 * @param json
 */
export function jsonToFormData(json: any): FormData {
    const formData = new FormData();
    Object.keys(json).forEach((key) => {
        formData.append(key, json[key]);
    });

    return formData;
}

/**
 * YYMMDD 형식의 생년월일을 날짜형태로 변경
 * @param birthday
 */
export function birthdayToDate(birthday: string): Date {
    const subStringLength = 2;
    const currentYY = new Date().getFullYear().toString().substring(2, 2 + subStringLength);
    const yy = birthday.substring(0, 0 + subStringLength);
    const mm = birthday.substring(2, 2 + subStringLength);
    const dd = birthday.substring(4, 4 + subStringLength);
    const yyyy = Number(currentYY) < Number(yy) ? '19' + yy : '20' + yy;
    return new Date(yyyy + '-' + mm + '-' + dd);
}

/**
 * 숫자 앞에 0을 붙인다
 * @param num
 */
export function zeroPad(num: number | string): string {
    return (num.toString().length >= 2 ? num : new Array(2 - num.toString().length + 1).join('0') + num).toString();
}

export function getCarrierYear(carrierYear: number): string {
    return `${carrierYear}년 ~ ${new Date().getFullYear()}년 (총 ${(new Date().getFullYear() - Number(carrierYear) + 1)}년)`;
}

/**
 * 휴대폰 번호에 - 하이픈 기호를 표시한다.
 *
 * @param {string} phone
 * @returns {string}
 */
export function getPhoneWithHyphen(phone: string) {
    return phone.replace(/(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/, '$1-$2-$3');
}

/**
 * 사업자 등록증 번호에 하이픈 추가
 * @param num
 */
export function getBusinessLicenseWithHyphen(num: string): string {
    return num.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
}

/**
 * 화물운송종사자격증 번호에 - 하이픈 기호를 표시한다.
 * @param num
 */
export function getTransportationLicenseWithHyphen(num: string): string {
    return num.replace(/(\d)(\d{2})(\d{6})/, '$1-$2-$3');
}

/**
 * 생년월일을 YY.MM.DD 로 표현
 * @param birthday
 */
export function getBirthdayWithDot(birthday: string): string {
    return birthday.replace(/(\d{2})(\d{2})(\d{2})/, '$1.$2.$3')
}

/**
 * 휴대전화형식인지 체크
 * @param number
 */
export function validatePhone(number: string) {
    return /^(01[016789]-\d{4}-\d{4})|(01[016789]\d{4}\d{4})$/.test(number);
}

/**
 * 이름 체크
 * @param name
 * @param type
 */
export function validateName(name: string, type = false): boolean {
    if (name.length === 0) return !type;
    return /^[ㄱ-ㅎ|ㅏ-ㅣ|가-힣|0-9]{0,10}$/.test(name);
}

/**
 * 인풋값 체크
 * @param value
 */
export function validateInput(value: string | number): boolean {
    if (typeof value === 'string') return value ? true : false;
    else if (typeof value === 'number') return value !== 0 ? true : false;
    else return false;
}

/**
 * 길이체크
 * @param value
 * @param length
 */
export function validateLength(value: string, length: number): boolean {
    return value.length === length;
}

export function validationBirthDay(birthDay: string): boolean {
    return /([0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[1,2][0-9]|3[0,1]))/.test(birthDay);
}

export function validationEmailDomain(domain: string): boolean {
    return /(\w+\.)+[A-Za-z]+/.test(domain);
}

export function* range(num: number) {
    let i: number = -1;
    while (++i < num) yield i;
}

/**
 * 파일 삭제
 * @param inputRef
 */
export function deleteFile(inputRef: RefObject<HTMLInputElement>): void {
    if (!inputRef.current || !inputRef.current.files) return;
    inputRef.current.value = inputRef.current.defaultValue;
}

/**
 * 사진 업로드
 * @param inputRef
 * @param dir
 * @param maxSize
 */
export function imageUpload(inputRef: RefObject<HTMLInputElement>, dir: string, maxSize?: number): Promise<{ data: string; file: File }> {


    return new Promise((resolve, reject) => {
        if (!inputRef.current || !inputRef.current.files) return reject(new Error(ALERT_IMG_UPLOAD_NO_SEARCH));

        const file: File = inputRef.current.files[0];

        if (!file) return reject(new Error(ALERT_IMG_UPLOAD_NO_SEARCH));

        if (!file.type.startsWith('image')) {
            deleteFile(inputRef);
            return reject(new Error(ALERT_IMG_UPLOAD_TYPE));
        }

        if (!isAllowedFileType(file.type, ['jpg', 'jpeg', 'png'])) {
            return reject(new Error(ALERT_IMG_UPLOAD_EXTENSION));
        }

        if (maxSize && maxSize <= file.size) {
            deleteFile(inputRef);
            return reject(new Error(ALERT_IMG_UPLOAD_SIZE));
        }

        const formData = new FormData();
        formData.append('img', file);
        formData.append('dir', dir);

        apiFileUpload(formData)
            .then((response) => {
                const uploadedImageUrls = response.data;

                if (uploadedImageUrls[0]) {
                    resolve({ data: uploadedImageUrls[0], file });
                    return;
                }
                throw new Error();

            })
            .catch(() => {
                return reject(new Error(ALERT_IMG_UPLOAD_FAIL));
            });
    });
}

export interface ImageData {
  data: string;
  file: File;
  error?: string;
}

export function isAllowedFileType(fileType: string, allowedTypeList: string[]) {
  let extension = '';

  if (fileType[0] === '.') {
    extension = fileType.substring(1);
  }

  const splittedType = fileType.split('/');

  if (splittedType.length === 2) {
    extension = splittedType[1];
  }

  extension = extension.toLowerCase();

  return allowedTypeList.includes(extension);
}

/**
 * 사진 한 장 업로드
 * @param file
 * @param dir
 * @param maxSize
 */
export function uploadFileImage(file: File, dir: string, maxSize?: number): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image')) {
      return reject(new Error(ALERT_IMG_UPLOAD_TYPE));
    }

    if (!isAllowedFileType(file.type, ['jpg', 'jpeg', 'png'])) {
      return reject({ data: URL.createObjectURL(file), file, error: ALERT_IMG_UPLOAD_EXTENSION});
    }

    if (maxSize && maxSize <= file.size) {
      return reject({ data: URL.createObjectURL(file), file, error: ALERT_IMG_UPLOAD_SIZE});
    }

    const formData = new FormData();
      formData.append('img', file);
      formData.append('dir', dir);

      apiFileUpload(formData)
        .then((response) => {
          const uploadedImageUrls = response.data;

          if (uploadedImageUrls[0]) {
            resolve({ data: uploadedImageUrls[0], file });
            return;
          }
          throw new Error();
        })
        .catch(() => {
          return reject({ data: URL.createObjectURL(file), file, error: ALERT_IMG_UPLOAD_FAIL});
        });
  })
}

/**
 * 사진 여러 장 업로드
 * @param inputRef
 * @param dir
 * @param maxSize
 */
export function uploadFileImageList(inputRef: RefObject<HTMLInputElement>, dir: string, maxSize?: number): Promise<ImageData>[] {
  if (!inputRef.current || !inputRef.current.files) return [];

  const fileList: FileList = inputRef.current.files;

  if (!fileList) {
    return []
  };

  const promises: Promise<ImageData>[] = Array.from(fileList).map((file: File) => uploadFileImage(file, dir, maxSize));

  return promises;
}

export function getServiceStatus(status: string): string {
    const statusList: { [key: string]: any; } = {
        nodoc: '',
        doc_wait: '심사중',
        doc_reject: '서류 재확인',
        doc_checked: '서류 확인',
        interview_notice: '인터뷰 안내',
        interview_answer: '인터뷰 참여',
        interview_noanswer: '인터뷰 미응답',
        interview_progress: '인터뷰 참여',
        interview_approve: '인터뷰 승인',
        interview_nonapprove: '인터뷰 비승인',
        approve_check: '승인 검토',
        approve: '서비스 중',
        approve_reject: '승인 거절',
        inactive: '비활성',
        suspended: '일시정지',
        isTemp: '작성중',
    }
    return statusList[status];
}

export function getDocRejectComment(doc: string): string {
    const rejectComment: { [key: string]: any; } = {
        serviceImageValid: '활동사진 오류',
        introduceValid: '홍보메세지 오류',
        businessRegistrationImageValid: '사업자등록증 오류',
        familyRelationImageValid: '가족관계증명서  오류',
        transportationLicenseImageValid: '화물운송종사자 자격증명 오류 ',
        indemnityInsuranceImageValid: '이사 화물 배상 책임 보험 오류',
        arrangeLicenseImageValid: '주선 면허 허가증 오류',
        profileImageValid: '프로필 사진 오류',
        memberValid: '직원 정보 오류',
        carImageValid: '차량사진 오류'

    }
    return rejectComment[doc];
}

export function getServiceType(serviceType?: string): string {
    switch (serviceType) {
        case 'smallmove':
            return 'small_move';
        case 'small_move':
            return 'smallmove';
        case 'familymove':
            return 'family_move';
        case 'family_move':
            return 'familymove';
        default:
            return '';
    }
}

export function getUrlServiceType(serviceType?: string): 'smallmove' | 'familymove' | 'movecleaning' | '' {
  switch (serviceType) {
    case 'smallmove':
    case 'small_move': {
      return 'smallmove';
    }

    case 'familymove':
    case 'family_move': {
      return 'familymove';
    }

    case 'movecleaning':
    case 'move_cleaning': {
      return 'movecleaning';
    }

    default: {
      return '';
    }
  }
}

export function isSameServiceType(serviceTypeA: string, serviceTypeB: string) {
    const isSmallMove = (service: string) => service === 'smallmove' || service === 'small_move';
    const isFamilyMove = (service: string) => service === 'familymove' || service === 'family_move';
    const isMoveCleaning = (service: string) => service === 'movecleaning' || service === 'move_cleaning';

    if (isSmallMove(serviceTypeA) && isSmallMove(serviceTypeB)) {
        return true;
    }

    if (isFamilyMove(serviceTypeA) && isFamilyMove(serviceTypeB)) {
        return true;
    }

    if (isMoveCleaning(serviceTypeA) && isMoveCleaning(serviceTypeB)) {
        return true;
    }

    return false;
}

export function getType(target: any) {
  return Object.prototype.toString.call(target).slice(8, -1);
}

export function isString(target: any): target is string {
  return getType(target) === 'String';
}

export function isNumber(target: any): target is number {
  return getType(target) === 'Number';
}

export function isBoolean(target: any): target is boolean {
  return getType(target) === 'Boolean';
}

export function isNull(target: any): target is null {
  return getType(target) === 'Null';
}

export function isUndefined(target: any): target is undefined {
  return getType(target) === 'Undefined';
}

export interface AnyObject {
  [key: string]: any;
}

export function isObject(target: any): target is AnyObject {
  return getType(target as AnyObject) === 'Object';
}

export function isArray(target: any): target is any[] {
  return getType(target) === 'Array';
}

export function isDate(target: any): target is Date {
  return getType(target) === 'Date';
}

export function isRegExp(target: any): target is RegExp {
  return getType(target) === 'RegExp';
}

export function isFunction(target: any): target is Function {
  return getType(target) === 'Function';
}

export function getFulfilledImageList(imageObject: ImageObject): string[] {
  const fulfilledDataList: string[] = Object.entries(imageObject).reduce((prev, [, imageInfo]) => {
    if (imageInfo.imageState === 'fulfilled') {
      prev.push(imageInfo.tempValue);
    }

    return prev;
  }, [] as string[]);

  return fulfilledDataList;
}

export function hasTotallyScrolled(scrollHeight: number, scrollTop: number, clientHeight: number): boolean {
  return scrollHeight - scrollTop === clientHeight;
}

export function isValidServiceState(serviceValid?: boolean, serviceName?: string, serviceImage?: string): boolean {
  return !!(serviceValid &&
    serviceName &&
    validateInput(serviceName) &&
    serviceImage &&
    validateInput(serviceImage));
}

export interface UploadImage {
  promise: Promise<ImageData>;
  data: ImageData;
}

export const getPendingImageList = (inputRef: React.RefObject<HTMLInputElement>): ImageData[] => {
  const pendingImageList: ImageData[] = [];

  if (!inputRef.current || !inputRef.current.files) {
    return pendingImageList;
  };

  for (let i = 0; i < inputRef.current.files.length; i++) {
    const file = inputRef.current.files[i];

    pendingImageList.push({ data: URL.createObjectURL(file), file });
  }

  return pendingImageList;
}

export const getUploadImageList = (inputRef: React.RefObject<HTMLInputElement>, imgDir: string): UploadImage[] => {
  const uploadImageList = [];

  const promises: Promise<ImageData>[] = uploadFileImageList(inputRef, imgDir, MEGA_BYTE_10);
  const pendingImageList = getPendingImageList(inputRef);

  for (let i = 0; i < promises.length; i++) {
    uploadImageList.push({ promise: promises[i], data: pendingImageList[i] });
  }

  return uploadImageList;
}

export const createUUID = () => {
  function s4() {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }
  return (
    s4() +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    '-' +
    s4() +
    s4() +
    s4()
  );
};
